import React, { useCallback, useState, useEffect, useMemo } from "react";
import ReactFlow, {
  Handle,
  Position,
  addEdge,
  Background,
  Controls,
  applyNodeChanges,
  applyEdgeChanges,
} from "reactflow";
import "reactflow/dist/style.css";
import { useDatabase } from "../contexts/DatabaseContext";
import { Box, Typography } from "@mui/material";
import { useDatabaseRelationships } from "../hooks/useDatabaseRelationships";
import { useAuth } from "../hooks/useAuth";
import { DropdownItem } from "../components/Dropdown/Dropdown.styled";
import Checkbox from "@mui/material/Checkbox";
import CheckIcon from "@mui/icons-material/Check";
import InputBase from "@mui/material/InputBase";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import IconButton from "@mui/material/IconButton";
import { useDbConnection } from "../hooks/useDbConnection";
import Button from "@mui/material/Button";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from "../constants";
import { gql, useMutation } from "@apollo/client";
import { StorageHelper } from "../utils/storageHelper";
import SyncIcon from "@mui/icons-material/Sync";
import ColumnDescriptionModal from "../components/ColumnDescriptionModal/ColumnDescriptionModal";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CircularProgress from "@mui/material/CircularProgress";
import { useGetSchemaWithDescriptions } from "../hooks/useGetSchemaWithDescriptions";

// Table icon SVG
const TableIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    style={{ marginRight: 6, verticalAlign: "middle" }}
  >
    <rect
      x="2"
      y="4"
      width="16"
      height="12"
      rx="2"
      fill="#fff"
      stroke="#3F49A7"
      strokeWidth="1.5"
    />
    <line x1="2" y1="8" x2="18" y2="8" stroke="#3F49A7" strokeWidth="1.2" />
    <line x1="7" y1="4" x2="7" y2="16" stroke="#3F49A7" strokeWidth="1.2" />
  </svg>
);

const HalfDot = ({
  side = "left",
  color = "#3F49A7",
  border = "#fff",
  size = 20,
  borderWidth = 3,
  style = {},
}) => {
  return (
    <svg
      width={size}
      height={size}
      style={{
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        [side]: side === "left" ? -26 : -26,
        zIndex: 3,
        pointerEvents: "auto",
        ...style,
      }}
    >
      <defs>
        <clipPath id={`half-dot-${side}`}>
          {side === "left" ? (
            <rect x={0} y={0} width={size / 2} height={size} />
          ) : (
            <rect x={size / 2} y={0} width={size / 2} height={size} />
          )}
        </clipPath>
      </defs>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={(size - borderWidth) / 2}
        fill={color}
        stroke={border}
        strokeWidth={borderWidth}
        filter="drop-shadow(0 2px 8px #3F49A720)"
        clipPath={`url(#half-dot-${side})`}
      />
    </svg>
  );
};

const TableNode = ({ data, id, onColumnClick, onGenerateDescriptions, generatingDescriptions }) => (
  <div
    style={{
      background: "#fff",
      border: "2.5px solid #3F49A7",
      borderRadius: 16,
      minWidth: 260,
      boxShadow: "0 6px 24px #3F49A720",
      overflow: "visible",
      fontFamily: "Inter, sans-serif",
      cursor: "grab",
    }}
  >
    <div
      style={{
        background: "#3F49A7",
        color: "#fff",
        padding: "14px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontWeight: 800,
        fontSize: 20,
        letterSpacing: 1,
        borderTopLeftRadius: 14,
        borderTopRightRadius: 14,
        position: "relative",
        zIndex: 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", pointerEvents: "none" }}>
        <TableIcon />
        {data.label}
      </div>
      <Tooltip title="Generate AI Descriptions" arrow>
        <IconButton
          size="small"
          className="nodrag nopan"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            console.log("AI button clicked for table:", data.label);
            if (onGenerateDescriptions && !generatingDescriptions) {
              onGenerateDescriptions(data.label);
            }
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          disabled={generatingDescriptions}
          sx={{
            color: "#fff",
            padding: "4px",
            pointerEvents: "auto",
            zIndex: 10,
            position: "relative",
            cursor: "pointer",
            "&:hover": { background: "rgba(255,255,255,0.2)" },
            "&:disabled": { color: "rgba(255,255,255,0.5)", cursor: "not-allowed" }
          }}
        >
          {generatingDescriptions ? (
            <CircularProgress size={16} sx={{ color: "#fff" }} />
          ) : (
            <AutoAwesomeIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Tooltip>
    </div>
    <div style={{ padding: 16 }}>
      {data.columns.map((col, idx) => (
        <div
          key={col.id}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 16,
            padding: "10px 0",
            borderRadius: 8,
            marginBottom: 4,
            position: "relative",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f7fa")}
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "transparent")
          }
        >
          <HalfDot side="left" />
          <Handle
            type="target"
            position={Position.Left}
            id={`target-${id}-${col.id}`}
            style={{
              top: "50%",
              transform: "translateY(-50%)",
              left: -26,
              position: "absolute",
              width: 20,
              height: 20,
              opacity: 0, // invisible but interactive
              zIndex: 4,
              cursor: "pointer",
            }}
          />
          <span
            style={{
              fontWeight: 600,
              letterSpacing: 0.5,
              cursor: "pointer",
              transition: "color 0.2s",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onColumnClick) {
                onColumnClick(data.label, col.name);
              }
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#3F49A7")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#333")}
          >
            {col.name}
          </span>
          <span
            style={{
              color: "#3F49A7",
              background: "#eaf2fa",
              borderRadius: 16,
              padding: "4px 16px",
              fontSize: 14,
              marginLeft: 12,
              fontFamily: "monospace",
              fontWeight: 700,
              boxShadow: "0 1px 4px #3F49A720",
            }}
          >
            {col.textDatatype}
          </span>
          <HalfDot side="right" />
          <Handle
            type="source"
            position={Position.Right}
            id={`source-${id}-${col.id}`}
            style={{
              top: "50%",
              transform: "translateY(-50%)",
              right: -26,
              position: "absolute",
              width: 20,
              height: 20,
              opacity: 0, // invisible but interactive
              zIndex: 4,
              cursor: "pointer",
            }}
          />
        </div>
      ))}
    </div>
  </div>
);

// TableNode component wrapper to accept onColumnClick
const TableNodeWrapper = ({ onColumnClick, onGenerateDescriptions, generatingDescriptions, ...props }) => (
  <TableNode
    {...props}
    onColumnClick={onColumnClick}
    onGenerateDescriptions={onGenerateDescriptions}
    generatingDescriptions={generatingDescriptions}
  />
);

// GraphQL mutation for syncing relationships to Neo4j
const CREATE_NEO4J_RELATIONSHIPS_BATCH = gql`
  mutation CreateNeo4jRelationshipsBatch($inputs: [CreateNeo4jRelationshipInput!]!) {
    CreateNeo4jRelationshipsBatch(inputs: $inputs) {
      success
      message
      fromNode
      toNode
      relationshipType
      level
    }
  }
`;

const DbDiagramView = () => {
  const { databaseState, updateDatabaseState } = useDatabase();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [search, setSearch] = useState("");
  const {
    getSnowflakeTableNames,
    addUserTableSelection,
    getUserTableSelection,
    updateUserTableSelection,
    getConnectionDetails,
    getPostgresConnectionDetails,
  } = useDbConnection();
  const [snowflakeTables, setSnowflakeTables] = useState([]);
  const [postgresTablesData, setPostgresTablesData] = useState([]);
  const [tableNamesLoading, setTableNamesLoading] = useState(false);
  const [tableNamesError, setTableNamesError] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveMode, setSaveMode] = useState("save"); // 'save' or 'update'
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState({ tableName: null, columnName: null });
  const [generatingDescriptions, setGeneratingDescriptions] = useState({});
  const [descriptionMessage, setDescriptionMessage] = useState(null);
  const [descriptionError, setDescriptionError] = useState(null);

  // Check if we're viewing postgres tables
  const isPostgresView = location?.state?.source === 'postgres';

  // GraphQL mutation for syncing to Neo4j
  const [syncToNeo4jMutation] = useMutation(CREATE_NEO4J_RELATIONSHIPS_BATCH);

  // GraphQL hook for generating descriptions
  const { generateDescriptions } = useGetSchemaWithDescriptions();

  const userId = user?.userDetails?.user_id;
  // For postgres views (uploaded Excel files), use 0 as userDbId since they don't have a datasourceId
  const userDbId = isPostgresView ? 0 : databaseState?.datasourceId;
  const {
    addRelationship,
    getRelationships,
    deleteRelationships,
    loading: relationshipsLoading,
    error: relationshipsError,
  } = useDatabaseRelationships();

  useEffect(() => {
    if (userId && userDbId !== undefined && userDbId !== null) {
      loadRelationships();
    }
  }, [userId, userDbId]);

  const loadRelationships = async () => {
    try {
      const result = await getRelationships(userDbId);
      if (result.success) {
        setRelationships(result.data);
      } else {
        setRelationships([]);
      }
    } catch (error) {
      setRelationships([]);
    }
  };

  useEffect(() => {
    if (relationships && relationships.length > 0 && nodes.length > 0) {
      const savedEdges = relationships
        .filter((rel) => rel.is_active)
        .map((rel) => {
          const sourceNode = nodes.find(
            (node) => node.data.label === rel.source_table
          );
          const targetNode = nodes.find(
            (node) => node.data.label === rel.destination_table
          );
          if (!sourceNode || !targetNode) return null;
          const sourceColumn = sourceNode.data.columns.find(
            (col) => col.name === rel.source_column
          );
          const targetColumn = targetNode.data.columns.find(
            (col) => col.name === rel.destination_column
          );
          if (!sourceColumn || !targetColumn) return null;
          return {
            id: `edge-${rel.id}`,
            source: sourceNode.id,
            target: targetNode.id,
            sourceHandle: `source-${sourceNode.id}-${sourceColumn.id}`,
            targetHandle: `target-${targetNode.id}-${targetColumn.id}`,
            type: "bezier",
            data: {
              relationshipId: rel.id,
              sourceTable: rel.source_table,
              destinationTable: rel.destination_table,
              sourceColumn: rel.source_column,
              destinationColumn: rel.destination_column,
            },
          };
        })
        .filter((edge) => edge !== null);
      setEdges(savedEdges);
    } else if (relationships && relationships.length === 0) {
      setEdges([]);
    }
  }, [relationships, nodes]);

  // Handler for column click
  const handleColumnClick = useCallback((tableName, columnName) => {
    setSelectedColumn({ tableName, columnName });
    setColumnModalOpen(true);
  }, []);

  // Handler for generating descriptions
  const handleGenerateDescriptions = useCallback(async (tableName) => {
    if (!databaseState?.datasourceId || !user?.userDetails?.user_id) {
      setDescriptionError("Missing database connection information");
      return;
    }

    setGeneratingDescriptions(prev => ({ ...prev, [tableName]: true }));
    setDescriptionMessage(null);
    setDescriptionError(null);

    try {
      const dbType = databaseState.dbType || "snowflake";

      console.log("Generating descriptions for table:", tableName);

      // Use GraphQL mutation - same pattern as CSV download
      const result = await generateDescriptions({
        tableName,
        userId: parseInt(user.userDetails.user_id),
        userDbId: parseInt(databaseState.datasourceId),
        dbType,
      });

      console.log("GraphQL Response:", result);

      if (result.success) {
        const message = result.message || "Description generation job queued successfully";
        const jobId = result.jobId;

        setDescriptionMessage(`⏳ ${message} (Job ID: ${jobId})`);

        // Auto-hide message after 5 seconds
        setTimeout(() => setDescriptionMessage(null), 5000);
      } else {
        setDescriptionError(`Failed: ${result.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error generating descriptions:", error);
      setDescriptionError(`Error: ${error.message}`);
    } finally {
      setGeneratingDescriptions(prev => ({ ...prev, [tableName]: false }));
    }
  }, [databaseState, user, generateDescriptions]);

  // Memoize nodeTypes to prevent ResizeObserver errors
  const nodeTypes = useMemo(
    () => ({
      tableNode: (props) => (
        <TableNodeWrapper
          {...props}
          onColumnClick={handleColumnClick}
          onGenerateDescriptions={handleGenerateDescriptions}
          generatingDescriptions={generatingDescriptions[props.data.label]}
        />
      ),
    }),
    [handleColumnClick, handleGenerateDescriptions, generatingDescriptions]
  );

  // Suppress ResizeObserver errors (common React Flow issue, non-critical)
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (
        typeof args[0] === 'string' &&
        (args[0].includes('ResizeObserver loop limit exceeded') ||
          args[0].includes('ResizeObserver loop completed with undelivered notifications'))
      ) {
        return;
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  // Only show selected tables in the diagram
  useEffect(() => {
    const tablesData = isPostgresView ? postgresTablesData : databaseState.tablesData;

    if (tablesData && tablesData.length > 0) {
      // If no tables are selected, show none
      if (selectedTables.length === 0) {
        setNodes([]);
        return;
      }
      const tableNodes = tablesData.flatMap((db, dbIndex) =>
        db.children
          .filter(
            (table) =>
              selectedTables.includes(table.name) ||
              selectedTables.includes(table.id)
          )
          .map((table, tableIndex) => ({
            id: table.id,
            type: "tableNode",
            data: {
              label: table.name,
              columns: table.children.map((column) => ({
                id: column.id,
                name: column.name,
                textDatatype: column.textDatatype || "TEXT",
              })),
            },
            position: {
              x: 100 + dbIndex * 300 + tableIndex * 350,
              y: 100 + dbIndex * 200 + tableIndex * 250,
            },
            draggable: true,
          }))
      );
      setNodes(tableNodes);
    }
  }, [databaseState.tablesData, postgresTablesData, selectedTables, isPostgresView]);

  useEffect(() => {
    const fetchTableNames = async () => {
      if (isPostgresView) {
        // Fetch postgres tables
        if (userId) {
          setTableNamesLoading(true);
          setTableNamesError(null);
          try {
            const result = await getPostgresConnectionDetails(userId);
            if (result.success && result.data?.tablesData) {
              let tablesData = result.data.tablesData;
              if (typeof tablesData === "string") {
                try {
                  tablesData = JSON.parse(tablesData);
                } catch (e) {
                  // Error parsing tablesData
                }
              }
              // Store full postgres data structure
              setPostgresTablesData(Array.isArray(tablesData) ? tablesData : []);
              // Extract table names from postgres data structure
              const tableNames = [];
              if (Array.isArray(tablesData)) {
                tablesData.forEach((db) => {
                  if (db.children && Array.isArray(db.children)) {
                    db.children.forEach((table) => {
                      tableNames.push(table.name);
                    });
                  }
                });
              }
              setSnowflakeTables(tableNames);
            } else {
              setTableNamesError(result.error || "Failed to fetch postgres tables");
              setSnowflakeTables([]);
            }
          } catch (err) {
            setTableNamesError(err.message || "Failed to fetch postgres tables");
            setSnowflakeTables([]);
          } finally {
            setTableNamesLoading(false);
          }
        }
      } else {
        // Fetch snowflake tables
        if (userId && userDbId !== undefined && userDbId !== null) {
          setTableNamesLoading(true);
          setTableNamesError(null);
          try {
            const result = await getSnowflakeTableNames(userId, userDbId);
            if (result.success) {
              setSnowflakeTables(result.data || []);
            } else {
              setTableNamesError(result.error || "Failed to fetch table names");
              setSnowflakeTables([]);
            }
          } catch (err) {
            setTableNamesError(err.message || "Failed to fetch table names");
            setSnowflakeTables([]);
          } finally {
            setTableNamesLoading(false);
          }
        }
      }
    };
    fetchTableNames();
  }, [userId, userDbId, getSnowflakeTableNames, getPostgresConnectionDetails, isPostgresView]);

  // Pre-select tables from user selection on mount
  useEffect(() => {
    const fetchUserSelection = async () => {
      if (userId && userDbId !== undefined && userDbId !== null) {
        try {
          const result = await getUserTableSelection(userId, userDbId);

          if (result.success) {
            // For postgres view, use uploadedUserSelected; for snowflake, use data (tableNames)
            if (isPostgresView) {
              // In postgres view, use uploadedUserSelected
              if (Array.isArray(result.uploadedUserSelected) && result.uploadedUserSelected.length > 0) {
                setSelectedTables(result.uploadedUserSelected);
                setSaveMode("update");
              } else if (Array.isArray(result.data) && result.data.length > 0) {
                // If no uploaded tables selected yet, but there are snowflake tables, start fresh
                setSelectedTables([]);
                setSaveMode(result.data.length > 0 ? "update" : "save");
              } else {
                setSelectedTables([]);
                setSaveMode("save");
              }
            } else {
              // In snowflake view, use data (tableNames)
              if (Array.isArray(result.data) && result.data.length > 0) {
                setSelectedTables(result.data.map((t) => t.tableName));
                setSaveMode("update");
              } else if (Array.isArray(result.uploadedUserSelected) && result.uploadedUserSelected.length > 0) {
                // If no snowflake tables selected yet, but there are uploaded tables, start fresh
                setSelectedTables([]);
                setSaveMode(result.uploadedUserSelected.length > 0 ? "update" : "save");
              } else {
                setSelectedTables([]);
                setSaveMode("save");
              }
            }
          } else {
            setSelectedTables([]);
            setSaveMode("save");
          }
        } catch (err) {
          setSelectedTables([]);
          setSaveMode("save");
        }
      }
    };
    fetchUserSelection();
  }, [userId, userDbId, getUserTableSelection, isPostgresView]);

  const getTableAndColumnInfo = useCallback((handleId) => {
    if (!handleId) return null;
    const parts = handleId.split("-");
    if (parts.length !== 3) return null;
    const [handleType, tableId, columnId] = parts;
    const tablesData = isPostgresView ? postgresTablesData : databaseState.tablesData;

    for (const db of tablesData || []) {
      for (const table of db.children) {
        if (String(table.id) === String(tableId)) {
          for (const column of table.children) {
            if (String(column.id) === String(columnId)) {
              return {
                tableName: table.name,
                columnName: column.name,
                columnType: column.textDatatype,
                handleType: handleType, // 'source' or 'target'
              };
            }
          }
        }
      }
    }
    return null;
  }, [isPostgresView, postgresTablesData, databaseState.tablesData]);

  const onConnect = useCallback(
    async (params) => {
      const sourceInfo = getTableAndColumnInfo(params.sourceHandle);
      const targetInfo = getTableAndColumnInfo(params.targetHandle);

      setEdges((eds) => addEdge({ ...params, type: "bezier" }, eds));
      if (sourceInfo && targetInfo && userId && userDbId !== undefined && userDbId !== null) {
        try {


          const result = await addRelationship({
            userId,
            userDbId,
            sourceTable: sourceInfo.tableName,
            destinationTable: targetInfo.tableName,
            sourceColumn: sourceInfo.columnName,
            destinationColumn: targetInfo.columnName,
          });


          if (result.success) {
            await loadRelationships();
          } else {
            setEdges((eds) => eds.filter((edge) => edge.id !== params.id));
          }
        } catch (error) {
          setEdges((eds) => eds.filter((edge) => edge.id !== params.id));
        }
      } else {

      }
    },
    [databaseState.tablesData, postgresTablesData, isPostgresView, userId, userDbId, addRelationship, getTableAndColumnInfo, loadRelationships]
  );

  // Persist selection / removals for controlled edges (required for keyboard delete + correct selection).
  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  // React Flow removes edges via onEdgesChange; notify backend for saved relationships.
  const onEdgesDelete = useCallback(
    (edgesToDelete) => {
      if (!edgesToDelete?.length) return;
      const persist = edgesToDelete.filter(
        (edge) =>
          edge.data?.relationshipId &&
          userId &&
          userDbId !== undefined &&
          userDbId !== null
      );
      if (!persist.length) return;
      void (async () => {
        try {
          for (const edge of persist) {
            await deleteRelationships(
              userDbId,
              edge.data.relationshipId
            );
          }
          await loadRelationships();
        } catch (error) {
          await loadRelationships();
        }
      })();
    },
    [userId, userDbId, deleteRelationships, loadRelationships]
  );

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  // Sync relationships to Neo4j
  const handleSyncToNeo4j = async () => {
    const user_db_id = StorageHelper.get("selected_user_db_id");

    if (!user_db_id) {
      setSyncError("Please select a database first");
      return;
    }

    setSyncLoading(true);
    setSyncMessage(null);
    setSyncError(null);

    try {
      // When bdrelation is true, the backend fetches all relationships from database_relationships table
      // The from, to, relationship, and level fields are ignored when bdrelation is true
      // Using the format specified by the user
      const inputs = [
        {
          from: {
            table: "null",
            column: "null",
          },
          to: {
            table: "null",
            column: "null",
          },
          relationship: "null",
          level: "null",
          bdrelation: true,
          user_db_id: parseInt(user_db_id),
          db_type: "snowflake",
        },
      ];

      const { data } = await syncToNeo4jMutation({
        variables: { inputs },
      });

      if (data?.CreateNeo4jRelationshipsBatch) {
        const results = data.CreateNeo4jRelationshipsBatch;
        const successCount = results.filter((r) => r.success).length;
        const totalCount = results.length;

        if (successCount === totalCount) {
          setSyncMessage(`Successfully synced ${successCount} relationship(s) to Neo4j`);
        } else {
          setSyncMessage(
            `Synced ${successCount} out of ${totalCount} relationship(s) to Neo4j`
          );
        }
      } else {
        setSyncError("Failed to sync relationships to Neo4j");
      }
    } catch (error) {
      setSyncError(error.message || "Failed to sync relationships to Neo4j");
    } finally {
      setSyncLoading(false);
    }
  };

  // Use snowflakeTables for the list
  const allTables = snowflakeTables.map((name) => ({ id: name, name }));
  const allTableIds = allTables.map((table) => table.id);

  // Select All logic
  const allSelected =
    selectedTables.length === allTableIds.length && allTableIds.length > 0;
  const isIndeterminate =
    selectedTables.length > 0 && selectedTables.length < allTableIds.length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTables(allTableIds);
    } else {
      setSelectedTables([]);
    }
  };

  const handleSelectTable = (tableId) => (e) => {
    if (e.target.checked) {
      setSelectedTables((prev) => [...prev, tableId]);
    } else {
      setSelectedTables((prev) => prev.filter((id) => id !== tableId));
    }
  };

  // Filtered tables based on search
  const filteredTables = allTables.filter((table) =>
    table.name.toLowerCase().includes(search.toLowerCase())
  );

  // Validation checks
  const tablesData = isPostgresView ? postgresTablesData : databaseState.tablesData;

  if (!isPostgresView && !databaseState.isConnected) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h5" color="text.secondary">
          No Database Connected
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please connect to a database to view the diagram
        </Typography>
      </Box>
    );
  }

  if (!tablesData || tablesData.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h5" color="text.secondary">
          No Tables Found
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isPostgresView
            ? "No saved tables are available"
            : "No tables are available in the connected database"}
        </Typography>
      </Box>
    );
  }

  return (
    <div style={{ width: "100%", height: "80vh", position: "relative" }}>
      {/* Done and Sync Buttons */}
      <Box sx={{ position: "absolute", top: 24, right: 24, zIndex: 20, display: "flex", gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<SyncIcon />}
          onClick={handleSyncToNeo4j}
          disabled={syncLoading}
          sx={{
            fontSize: 13,
            fontWeight: 600,
            borderRadius: 1,
            textTransform: "none",
            boxShadow: "none",
            backgroundColor: "#3F49A7",
            color: "#fff",
            "&:hover": { backgroundColor: "#2e357a" },
          }}
        >
          {syncLoading ? "Syncing..." : "Sync Ask FlashDice Relationships"}
        </Button>
      </Box>
      {/* Sync Messages */}
      {syncMessage && (
        <Box sx={{ position: "absolute", top: 70, right: 24, zIndex: 20 }}>
          <Typography
            variant="caption"
            sx={{
              color: "success.main",
              fontSize: 12,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              padding: "4px 8px",
              borderRadius: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            {syncMessage}
          </Typography>
        </Box>
      )}
      {syncError && (
        <Box sx={{ position: "absolute", top: 70, right: 24, zIndex: 20 }}>
          <Typography
            variant="caption"
            sx={{
              color: "error.main",
              fontSize: 12,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              padding: "4px 8px",
              borderRadius: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            {syncError}
          </Typography>
        </Box>
      )}
      {/* Description Generation Messages */}
      {descriptionMessage && (
        <Box sx={{ position: "absolute", top: 100, right: 24, zIndex: 20 }}>
          <Typography
            variant="caption"
            sx={{
              color: "success.main",
              fontSize: 12,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              padding: "4px 8px",
              borderRadius: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            {descriptionMessage}
          </Typography>
        </Box>
      )}
      {descriptionError && (
        <Box sx={{ position: "absolute", top: 100, right: 24, zIndex: 20 }}>
          <Typography
            variant="caption"
            sx={{
              color: "error.main",
              fontSize: 12,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              padding: "4px 8px",
              borderRadius: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            {descriptionError}
          </Typography>
        </Box>
      )}
      {/* Table Select List - floating card */}
      <Box
        sx={{
          position: "absolute",
          top: 24,
          left: 24,
          zIndex: 10,
          width: 260,
          height: 320,
          p: 0,
          border: "1.5px solid #e0e0e0",
          borderRadius: 1,
          background: "rgba(255,255,255,0.75)",
          backdropFilter: "blur(8px)",
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.18)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transition: "box-shadow 0.2s",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 1,
            pt: 0.7,
            pb: 0.7,
            borderBottom: "1px solid #f0f0f0",
            background: "rgba(250,251,252,0.95)",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "#3F49A7",
              fontSize: 13,
              textAlign: "center",
            }}
          >
            {isPostgresView ? "Saved Tables" : "Snowflake Tables"}
          </Typography>
        </Box>
        {/* Search input */}
        <Box
          sx={{
            p: 1,
            pt: 0.7,
            pb: 0.7,
            borderBottom: "1px solid #f0f0f0",
            background: "rgba(250,251,252,0.85)",
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <InputBase
            placeholder="Search tables..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: "100%",
              fontSize: 12,
              px: 0.7,
              py: 0.2,
              borderRadius: 2,
              background: "#f5f7fa",
              border: "1px solid #e0e0e0",
              boxShadow: "0 1px 2px rgba(63,73,167,0.04)",
              transition: "border 0.2s",
              "&:focus-within": {
                borderColor: "#3F49A7",
              },
            }}
            inputProps={{ style: { padding: 4, fontSize: 12 } }}
          />
        </Box>
        {/* Select All as DropdownItem */}
        <DropdownItem
          isSelected={allSelected}
          onClick={() => {
            if (allSelected) {
              setSelectedTables([]);
            } else {
              setSelectedTables(allTableIds);
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            userSelect: "none",
            minHeight: 30,
            px: 0.7,
            borderRadius: 0,
            fontSize: 12,
            fontWeight: 600,
            color: "#3F49A7",
            background: "transparent",
            "&:hover": { background: "rgba(63,73,167,0.07)" },
          }}
        >
          <Checkbox
            indeterminate={isIndeterminate}
            checked={allSelected}
            onChange={handleSelectAll}
            sx={{
              p: 0,
              mr: 0.7,
              color: "#3F49A7",
              "&.Mui-checked": { color: "#3F49A7" },
              "&.MuiCheckbox-indeterminate": { color: "#3F49A7" },
            }}
            icon={<span className="selection-indicator" />}
            checkedIcon={
              <span className="selection-indicator">
                <CheckIcon style={{ fontSize: 13, color: "#3F49A7" }} />
              </span>
            }
          />
          <span
            className="item-label"
            style={{ fontWeight: 600, fontSize: 12, color: "#3F49A7" }}
          >
            Select All Tables
          </span>
        </DropdownItem>
        <Divider sx={{ m: 0, borderColor: "#f0f0f0" }} />
        {/* Table list */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 0.7,
            pr: 0.3,
            "::-webkit-scrollbar": { width: 5 },
            "::-webkit-scrollbar-thumb": {
              background: "#e0e7ef",
              borderRadius: 3,
            },
            "::-webkit-scrollbar-track": { background: "transparent" },
          }}
        >
          {tableNamesLoading ? (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                mt: 2,
                fontSize: 12,
              }}
            >
              Loading tables...
            </Typography>
          ) : tableNamesError ? (
            <Typography
              variant="body2"
              sx={{
                color: "error.main",
                textAlign: "center",
                mt: 2,
                fontSize: 12,
              }}
            >
              {tableNamesError}
            </Typography>
          ) : filteredTables.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                mt: 2,
                fontSize: 12,
              }}
            >
              No tables found
            </Typography>
          ) : (
            filteredTables.map((table) => {
              const isSelected = selectedTables.includes(table.id);
              // Highlight search match
              const idx = table.name
                .toLowerCase()
                .indexOf(search.toLowerCase());
              let label;
              if (idx !== -1 && search) {
                label = (
                  <>
                    {table.name.substring(0, idx)}
                    <span
                      style={{
                        background: "#ffe082",
                        borderRadius: 2,
                        padding: "0 2px",
                        transition: "background 0.2s",
                        fontSize: 12,
                      }}
                    >
                      {table.name.substring(idx, idx + search.length)}
                    </span>
                    {table.name.substring(idx + search.length)}
                  </>
                );
              } else {
                label = table.name;
              }
              return (
                <DropdownItem
                  key={table.id}
                  isSelected={isSelected}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedTables((prev) =>
                        prev.filter((id) => id !== table.id)
                      );
                    } else {
                      setSelectedTables((prev) => [...prev, table.id]);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    userSelect: "none",
                    minHeight: 30,
                    px: 0.7,
                    borderRadius: 0,
                    fontSize: 12,
                    transition:
                      "background 0.18s, box-shadow 0.18s, border 0.18s",
                    "&:hover": {
                      background: "rgba(63,73,167,0.07)",
                      boxShadow: "0 2px 8px #3F49A720",
                    },
                    "&:active": {
                      background: "rgba(63,73,167,0.13)",
                    },
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={handleSelectTable(table.id)}
                    sx={{ p: 0, mr: 0.7 }}
                    icon={<span className="selection-indicator" />}
                    checkedIcon={
                      <span className="selection-indicator">
                        <CheckIcon style={{ fontSize: 13 }} />
                      </span>
                    }
                  />
                  <TableChartOutlinedIcon
                    sx={{
                      fontSize: 15,
                      color: "#3F49A7",
                      mr: 0.7,
                      opacity: 0.7,
                    }}
                  />
                  <Tooltip
                    title={table.name.length > 18 ? table.name : ""}
                    arrow
                    placement="top"
                  >
                    <span
                      className="item-label"
                      style={{
                        maxWidth: 100,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "inline-block",
                        verticalAlign: "middle",
                        fontSize: 12,
                      }}
                    >
                      {label}
                    </span>
                  </Tooltip>
                </DropdownItem>
              );
            })
          )}
        </Box>
        {/* Save button and message - for both snowflake and postgres tables */}
        <Box
          sx={{
            p: 1,
            pt: 0.5,
            borderTop: "1px solid #f0f0f0",
            background: "rgba(250,251,252,0.85)",
          }}
        >
          <Button
            variant="contained"
            size="small"
            color="primary"
            fullWidth
            disabled={selectedTables.length === 0 || saveLoading}
            onClick={async () => {
              setSaveLoading(true);
              setSaveMessage(null);
              setSaveError(null);
              try {
                let result;
                // Get current selection to preserve the other field
                const currentSelection = await getUserTableSelection(userId, userDbId);

                if (isPostgresView) {
                  // For postgres view, update uploaded_user_selected and preserve table_names
                  const currentSnowflakeTables = currentSelection.success && currentSelection.data
                    ? currentSelection.data.map((t) => t.tableName)
                    : [];

                  if (saveMode === "update") {
                    result = await updateUserTableSelection(
                      userId,
                      userDbId,
                      currentSnowflakeTables, // Preserve existing snowflake tables
                      selectedTables // Update uploaded tables
                    );
                  } else {
                    result = await addUserTableSelection(
                      userId,
                      userDbId,
                      currentSnowflakeTables, // Preserve existing snowflake tables
                      selectedTables // Add uploaded tables
                    );
                  }
                } else {
                  // For snowflake view, update table_names and preserve uploaded_user_selected
                  const currentUploadedTables = currentSelection.success ? (currentSelection.uploadedUserSelected || []) : [];

                  if (saveMode === "update") {
                    result = await updateUserTableSelection(
                      userId,
                      userDbId,
                      selectedTables, // Update snowflake tables
                      currentUploadedTables // Preserve existing uploaded tables
                    );
                  } else {
                    result = await addUserTableSelection(
                      userId,
                      userDbId,
                      selectedTables, // Add snowflake tables
                      currentUploadedTables // Preserve existing uploaded tables
                    );
                  }
                }

                if (result.success) {
                  setSaveMessage(
                    saveMode === "update"
                      ? "Selection updated!"
                      : "Selection saved!"
                  );
                  setSaveMode("update");

                  // For snowflake view, fetch latest connection details and update databaseState
                  if (!isPostgresView) {
                    const detailsResult = await getConnectionDetails(
                      userId,
                      userDbId
                    );
                    if (
                      detailsResult.success &&
                      detailsResult.data &&
                      detailsResult.data.tablesData
                    ) {
                      let tablesData = detailsResult.data.tablesData;
                      if (typeof tablesData === "string") {
                        try {
                          tablesData = JSON.parse(tablesData);
                        } catch (e) {
                          // Error parsing tablesData
                        }
                      }
                      localStorage.setItem(
                        "snowflake_tables_data",
                        JSON.stringify(tablesData)
                      );

                      updateDatabaseState({
                        isConnected: true,
                        datasourceId: userDbId,
                        user_db_id: userDbId,
                        tablesData: tablesData,
                      });
                    }
                  }
                } else {
                  setSaveError(result.error || "Failed to save selection");
                }
              } catch (err) {
                setSaveError(err.message || "Failed to save selection");
              } finally {
                setSaveLoading(false);
              }
            }}
            sx={{
              mt: 0.5,
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 1,
              textTransform: "none",
              boxShadow: "none",
              backgroundColor: "#3F49A7",
              color: "#fff",
              "&:hover": { backgroundColor: "#2e357a" },
            }}
          >
            {saveLoading
              ? saveMode === "update"
                ? "Updating..."
                : "Saving..."
              : saveMode === "update"
                ? "Update"
                : "Save"}
          </Button>
          {saveMessage && (
            <Typography
              variant="caption"
              sx={{
                color: "success.main",
                mt: 0.5,
                display: "block",
                textAlign: "center",
              }}
            >
              {saveMessage}
            </Typography>
          )}
          {saveError && (
            <Typography
              variant="caption"
              sx={{
                color: "error.main",
                mt: 0.5,
                display: "block",
                textAlign: "center",
              }}
            >
              {saveError}
            </Typography>
          )}
        </Box>
      </Box>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onConnect={onConnect}
        onEdgesChange={onEdgesChange}
        onEdgesDelete={onEdgesDelete}
        onNodesChange={onNodesChange}
        fitView
        deleteKeyCode={["Backspace", "Delete"]}
      >
        <Background />
        <Controls />
      </ReactFlow>

      <ColumnDescriptionModal
        open={columnModalOpen}
        onClose={() => {
          setColumnModalOpen(false);
          setSelectedColumn({ tableName: null, columnName: null });
        }}
        tableName={selectedColumn.tableName}
        columnName={selectedColumn.columnName}
        userDbId={userDbId}
      />
    </div>
  );
};

export default DbDiagramView;
